import { BadgeCheckIcon } from '@heroicons/react/solid';

import Card from '../components/common/Card';
import FormInput from '../components/common/forms/FormInput';
import FormSelect from '../components/common/forms/FormSelect';
import FormSubmit from '../components/common/forms/FormSubmit';
import FormTextArea from '../components/common/forms/FormTextArea';
import { DefaultLayout } from '../components/layout/layout';

import { useUser } from '../hooks/useUser';

import { scrollToTop } from '../utils/scrolling';

import { supabase } from '../services/supabase/client';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

ContactPage.getLayout = (page) => {
    return <DefaultLayout>{page}</DefaultLayout>;
};

export const getStaticProps = async ({ locale }) => {
    return {
        props: {
            ...(await serverSideTranslations(locale, ['common', 'contact'])),
        },
    };
};

export default function ContactPage() {
    const { user, userData } = useUser();
    const { t } = useTranslation();

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const [name, setName] = useState(userData?.display_name ?? '');
    const [email, setEmail] = useState(user?.email ?? '');

    useEffect(() => {
        if (user && userData) {
            setName(userData.display_name);
            setEmail(user.email);
        }
    }, [user, userData]);

    const [projectType, setProjectType] = useState('');
    const [budget, setBudget] = useState('');

    const [message, setMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            name.length === 0 ||
            email.length === 0 ||
            projectType.length === 0 ||
            budget.length === 0 ||
            message.length === 0
        ) {
            toast.error('All fields are required.');
            scrollToTop();
            return;
        }

        setLoading(true);

        try {
            const { error } = await supabase.from('inquiries').insert(
                {
                    name,
                    email,
                    project_type: projectType,
                    budget,
                    message,
                },
                { returning: 'minimal' },
            );

            if (error) {
                throw error;
            } else {
                setSubmitted(true);
                toast.success('Your inquiry has been sent!');
            }
        } catch (error) {
            toast.error('Something went wrong.');
        } finally {
            setLoading(false);
            scrollToTop();
        }
    };

    return (
        <div className="flex flex-col items-center justify-center p-4 pt-16 md:px-16 lg:px-64">
            <div className="w-full text-center font-mono text-xl md:text-2xl lg:text-3xl font-bold">
                {submitted
                    ? t('contact:inquiry_sent')
                    : t('contact:contact_greetings')}
            </div>

            {submitted ? (
                <BadgeCheckIcon className="h-40 w-40 text-green-500" />
            ) : (
                <Card
                    disableHoverEffect={true}
                    className="w-full mx-auto mt-16 p-4 dark:bg-zinc-800/30 md:p-8 lg:p-12"
                >
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
                        <FormInput
                            id="name"
                            label={t('common:name')}
                            value={name}
                            placeholder={t('common:display_name_placeholder')}
                            required={true}
                            setter={setName}
                        />
                        <FormInput
                            id="email"
                            label={t('common:email')}
                            value={email}
                            placeholder={t('common:email_placeholder')}
                            required={true}
                            setter={setEmail}
                        />
                        <FormSelect
                            className="xl:col-span-1 col-span-full"
                            id="project-type"
                            label={t('contact:project_type')}
                            options={[
                                { value: '', label: '-' },
                                {
                                    value: 'website',
                                    label: t('contact:website'),
                                },
                                {
                                    value: 'mobile-app',
                                    label: t('contact:mobile_app'),
                                },
                                { value: 'other', label: t('common:other') },
                            ]}
                            required={true}
                            setter={setProjectType}
                        />
                    </div>

                    <FormSelect
                        id="budget"
                        label={t('contact:budget')}
                        options={[
                            { value: '', label: '-' },
                            {
                                value: 'less-than-1000-usd',
                                label: t('common:less_than') + ' $1,000',
                            },
                            {
                                value: '1000-to-5000-usd',
                                label: '$1,000 ' + t('common:to') + ' $5,000',
                            },
                            {
                                value: '5000-to-10000-usd',
                                label: '$5,000 ' + t('common:to') + ' $10,000',
                            },
                            {
                                value: '10000-to-20000-usd',
                                label: '$10,000 ' + t('common:to') + ' $20,000',
                            },
                            {
                                value: '20000-to-50000-usd',
                                label: '$20,000 ' + t('common:to') + ' $50,000',
                            },
                            {
                                value: '50000-to-100000-usd',
                                label:
                                    '$50,000 ' + t('common:to') + ' $100,000',
                            },
                            {
                                value: 'more-than-100000-usd',
                                label: t('common:more_than') + ' $100,000',
                            },
                        ]}
                        required={true}
                        setter={setBudget}
                    />

                    <FormTextArea
                        id="message"
                        label={t('common:additional_details')}
                        type="textarea"
                        placeholder={t('contact:i_want_to') + ' ...'}
                        required={true}
                        setter={setMessage}
                    />

                    <div className="mt-8 flex w-full justify-center">
                        <FormSubmit
                            label={t('common:submit')}
                            loadingLabel={t('common:submitting')}
                            loading={loading}
                            onClick={handleSubmit}
                        />
                    </div>
                </Card>
            )}
        </div>
    );
}
